import React, { useState, useEffect, useRef, useCallback } from 'react';
import Image from 'next/image';
import { useTranslation } from 'next-i18next';
import AgentCardEmailIcon from 'components/icon/agent-card-email-icon';
import AgentCardPhoneIcon from 'components/icon/agent-card-phone-icon';
import AgentCardCopyIcon from 'components/icon/agent-card-copy-icon';
import { AnalyticsController } from 'analytics/analytics-controller';
import {
  GTM_MLML_LISTING_EMAIL_COPY_CLICK,
  GTM_MLML_LISTING_PHONE_COPY_CLICK,
} from 'constants/events';
import { Namespaces, LOCALE_LISTING_PATHS } from 'types/locales';
import type { MyListingAgent } from 'hooks/use-my-listing-agents';
import { getAgentPhotoSrcForAvatar } from 'utils/agent-photo-url';
import { fetchAgentDetailsByNumericKey } from 'utils/agent-endpoint';
import useRecaptcha from 'hooks/use-recaptcha';
import InitialsAvatar from 'components/initials-avatar';
import styles from './style.module.scss';

function agentNeedsNumericFallback(agent: MyListingAgent): boolean {
  if (agent.isMlmlAgent || agent.agentKeyNumeric == null) return false;
  const hasPhoto = Boolean(getAgentPhotoSrcForAvatar(agent.photo));
  const hasEmail = Boolean(agent.email?.trim());
  const hasPhone = Boolean(agent.phone?.trim());
  return !hasPhoto || !hasEmail || !hasPhone;
}

const CAROUSEL_ROTATION_INTERVAL_MILLISECONDS = 8000;

type AgentLayout = 'row' | 'stacked';

interface AgentSlideProps {
  agent: MyListingAgent;
  layout: AgentLayout;
  inCarousel?: boolean;
  recaptchaReady: boolean;
}

interface AgentSlideLayoutProps {
  agent: MyListingAgent;
  imgSrc: string | null;
  showInitials: boolean;
  onAvatarError: () => void;
  agentLabel: string;
  onEmailCopy: () => void;
  onPhoneCopy: () => void;
  inCarousel?: boolean;
}

function LayoutStacked({
  agent,
  imgSrc,
  showInitials,
  onAvatarError,
  agentLabel,
  onEmailCopy,
  onPhoneCopy,
}: AgentSlideLayoutProps) {
  return (
    <div className={styles['card-stacked']}>
      <p className={styles['courtesy-label']}>{agentLabel}</p>
      {imgSrc ? (
        <Image
          src={imgSrc}
          alt={agent.name}
          className={styles['avatar-stacked']}
          width={120}
          height={120}
          onError={onAvatarError}
        />
      ) : (
        <InitialsAvatar
          name={agent.name}
          size={120}
          fontSize={40}
          circle
          bordered
          decorative={!showInitials}
          className={styles['avatar-initials-stacked']}
        />
      )}
      <p className={styles['agent-name-stacked']}>{agent.name}</p>
      {agent.designation && (
        <p className={styles['agent-designation-stacked']}>{agent.designation}</p>
      )}
      {agent.email && (
        <div className={styles['contact-row']}>
          <AgentCardEmailIcon className={styles['contact-icon']} />
          <a href={`mailto:${agent.email}`} className={styles['contact-text']}>
            {agent.email}
          </a>
          <AgentCardCopyIcon className={styles['copy-icon']} onClick={onEmailCopy} />
        </div>
      )}
      {agent.phone && (
        <div className={styles['contact-row']}>
          <AgentCardPhoneIcon className={styles['contact-icon']} />
          <a href={`tel:${agent.phone}`} className={styles['contact-text']}>
            {agent.phone}
          </a>
          <AgentCardCopyIcon className={styles['copy-icon']} onClick={onPhoneCopy} />
        </div>
      )}
    </div>
  );
}

function LayoutRow({
  agent,
  imgSrc,
  showInitials,
  onAvatarError,
  agentLabel,
  onEmailCopy,
  onPhoneCopy,
  inCarousel,
}: AgentSlideLayoutProps) {
  const cardClass = inCarousel ? `${styles.card} ${styles['card-in-carousel']}` : styles.card;

  return (
    <div className={cardClass}>
      <div className={styles['agent-row']}>
        <div className={styles['avatar-wrap']}>
          {imgSrc ? (
            <Image
              src={imgSrc}
              alt={agent.name}
              className={styles.avatar}
              width={90}
              height={90}
              onError={onAvatarError}
            />
          ) : (
            <InitialsAvatar
              name={agent.name}
              fill
              circle
              bordered
              decorative={!showInitials}
              className={styles['avatar-initials']}
            />
          )}
        </div>
        <div className={styles['agent-details']}>
          <div className={styles['agent-info']}>
            <p className={styles['courtesy-label']}>{agentLabel}</p>
            <p className={styles['agent-name']}>{agent.name}</p>
            {agent.designation && (
              <p className={styles['agent-designation']}>{agent.designation}</p>
            )}
          </div>
          {agent.email && (
            <div className={styles['contact-row']}>
              <AgentCardEmailIcon className={styles['contact-icon']} />
              <a href={`mailto:${agent.email}`} className={styles['contact-text']}>
                {agent.email}
              </a>
              <AgentCardCopyIcon className={styles['copy-icon']} onClick={onEmailCopy} />
            </div>
          )}
          {agent.phone && (
            <div className={styles['contact-row']}>
              <AgentCardPhoneIcon className={styles['contact-icon']} />
              <a href={`tel:${agent.phone}`} className={styles['contact-text']}>
                {agent.phone}
              </a>
              <AgentCardCopyIcon className={styles['copy-icon']} onClick={onPhoneCopy} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const AGENT_SLIDE_LAYOUT: Record<AgentLayout, React.FC<AgentSlideLayoutProps>> = {
  stacked: LayoutStacked,
  row: LayoutRow,
};

function AgentSlide({ agent, layout, inCarousel, recaptchaReady }: AgentSlideProps) {
  const hasValue = (value?: string) => Boolean(value?.trim());
  const initialImgSrc = getAgentPhotoSrcForAvatar(agent.photo);
  const canLookupPhotoByNumericKey = !agent.isMlmlAgent && agent.agentKeyNumeric != null;
  const shouldFetchFallbackInitially =
    canLookupPhotoByNumericKey &&
    (!initialImgSrc || !hasValue(agent.email) || !hasValue(agent.phone));
  const [imgSrc, setImgSrc] = useState<string | null>(initialImgSrc);
  const [resolvedEmail, setResolvedEmail] = useState(agent.email);
  const [resolvedPhone, setResolvedPhone] = useState(agent.phone);
  const [isResolvingPhoto, setIsResolvingPhoto] = useState<boolean>(
    !initialImgSrc && shouldFetchFallbackInitially
  );

  useEffect(() => {
    const directPhotoSrc = getAgentPhotoSrcForAvatar(agent.photo);
    const shouldFallbackEmail = !hasValue(agent.email);
    const shouldFallbackPhone = !hasValue(agent.phone);
    const shouldFetchFallback =
      canLookupPhotoByNumericKey &&
      (!directPhotoSrc || shouldFallbackEmail || shouldFallbackPhone);

    // eslint-disable-next-line react-hooks/set-state-in-effect -- re-sync derived photo/contact state when the agent prop changes before the optional async numeric-key fallback.
    setImgSrc(directPhotoSrc);
    setResolvedEmail(agent.email);
    setResolvedPhone(agent.phone);

    if (!shouldFetchFallback) {
      setIsResolvingPhoto(false);
      return;
    }

    if (!recaptchaReady) {
      setIsResolvingPhoto(!directPhotoSrc);
      return;
    }

    const numericKey = agent.agentKeyNumeric;
    if (numericKey == null) {
      setIsResolvingPhoto(false);
      return;
    }

    let isCancelled = false;
    setIsResolvingPhoto(!directPhotoSrc);

    fetchAgentDetailsByNumericKey(numericKey)
      .then(response => {
        if (isCancelled) return;
        if (!directPhotoSrc) {
          const resolvedPhotoSrc = getAgentPhotoSrcForAvatar(response?.photo);
          setImgSrc(resolvedPhotoSrc);
        }
        if (shouldFallbackEmail) {
          setResolvedEmail(response?.email ?? '');
        }
        if (shouldFallbackPhone) {
          setResolvedPhone(response?.phoneNumber ?? '');
        }
      })
      .catch(() => {
        if (isCancelled) return;
        if (!directPhotoSrc) {
          setImgSrc(null);
        }
      })
      .finally(() => {
        if (isCancelled) return;
        setIsResolvingPhoto(false);
      });

    return () => {
      isCancelled = true;
    };
  }, [agent.photo, agent.email, agent.phone, agent.agentKeyNumeric, canLookupPhotoByNumericKey, recaptchaReady]);

  const { t } = useTranslation(Namespaces.LISTING);
  const renderedAgent: MyListingAgent = {
    ...agent,
    email: resolvedEmail,
    phone: resolvedPhone,
  };

  const agentLabel = renderedAgent.isMlmlAgent
    ? t(LOCALE_LISTING_PATHS.AGENT_CARD_COURTESY_LABEL, { defaultValue: 'Courtesy of' })
    : t(LOCALE_LISTING_PATHS.AGENT_CARD_LISTING_AGENT_LABEL, { defaultValue: 'Listing Agent' });

  const handleEmailCopy = useCallback(() => {
    if (renderedAgent.email) navigator.clipboard.writeText(renderedAgent.email).catch(() => {});
    AnalyticsController.trackEvent(GTM_MLML_LISTING_EMAIL_COPY_CLICK);
  }, [renderedAgent.email]);

  const handlePhoneCopy = useCallback(() => {
    if (renderedAgent.phone) navigator.clipboard.writeText(renderedAgent.phone).catch(() => {});
    AnalyticsController.trackEvent(GTM_MLML_LISTING_PHONE_COPY_CLICK);
  }, [renderedAgent.phone]);

  const Layout = AGENT_SLIDE_LAYOUT[layout];

  return (
    <Layout
      agent={renderedAgent}
      imgSrc={imgSrc}
      showInitials={!imgSrc && !isResolvingPhoto}
      onAvatarError={() => setImgSrc(null)}
      agentLabel={agentLabel}
      onEmailCopy={handleEmailCopy}
      onPhoneCopy={handlePhoneCopy}
      inCarousel={inCarousel}
    />
  );
}

export interface MyListingAgentCardProps {
  agents: MyListingAgent[];
  layout?: AgentLayout;
}

interface MyListingAgentCardContentProps extends MyListingAgentCardProps {
  recaptchaReady: boolean;
}

function MyListingAgentCardContent({ agents, layout = 'row', recaptchaReady }: MyListingAgentCardContentProps) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval>>();
  const isCarousel = agents.length > 1;

  useEffect(() => {
    if (!isCarousel || isPaused) {
      if (timerRef.current) clearInterval(timerRef.current);
      return;
    }

    timerRef.current = setInterval(() => {
      setActiveIndex(prev => (prev + 1) % agents.length);
    }, CAROUSEL_ROTATION_INTERVAL_MILLISECONDS);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [isCarousel, isPaused, agents.length]);

  if (agents.length === 0) return null;

  if (!isCarousel) {
    return (
      <div className={styles.component}>
        <AgentSlide agent={agents[0]} layout={layout} recaptchaReady={recaptchaReady} />
      </div>
    );
  }

  return (
    <div
      className={styles.component}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      <div className={styles.carousel}>
        <div
          className={styles['carousel-track']}
          style={{ transform: `translateX(-${activeIndex * 100}%)` }}
        >
          {agents.map((agent, idx) => (
            <div key={`${agent.email || agent.name}-${idx}`} className={styles['carousel-slide']}>
              <AgentSlide agent={agent} layout={layout} inCarousel recaptchaReady={recaptchaReady} />
            </div>
          ))}
        </div>
        <div className={styles['carousel-dots']}>
          <div className={styles['carousel-dots-pill']}>
            {agents.map((_, idx) => (
              <button
                type="button"
                key={idx}
                className={`${styles['carousel-dot']} ${idx === activeIndex ? styles.active : ''}`}
                onClick={() => setActiveIndex(idx)}
                aria-label={`Go to agent ${idx + 1}`}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function MyListingAgentCardWithRecaptcha(props: MyListingAgentCardProps) {
  const { scriptLoaded, RecaptchaScript } = useRecaptcha();

  return (
    <>
      {RecaptchaScript}
      <MyListingAgentCardContent {...props} recaptchaReady={scriptLoaded} />
    </>
  );
}

export default function MyListingAgentCard(props: MyListingAgentCardProps) {
  const needsRecaptcha = props.agents.some(agentNeedsNumericFallback);

  if (!needsRecaptcha) {
    return <MyListingAgentCardContent {...props} recaptchaReady />;
  }

  return <MyListingAgentCardWithRecaptcha {...props} />;
}
